import React, { useCallback, useMemo, useState } from 'react';
import type {
  ColDef,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { Search } from 'lucide-react';
import { LopCrudPage } from '../../components/LopCrudPage';
import { LopSearchModal } from '../../components/LopSearchModal';
import { usePriceTypes } from '../../hooks/usePriceTypes';
import type {
  SpecialRate,
  SpecialRateRangeRequest,
} from '../../types/SpecialRate';
import { dateColumnProps } from '../../utils/dateColumn';
import { t, type MessageKey } from '../../i18n/t';
import './SpecialRatesGrid.css';
import { LopDateInput } from '../../components/LopDateInput';

const newSpecialRateDefaults = (): Partial<SpecialRate> => ({
  recId: 0,
  assetId: 0,
  dDate: null as unknown as string,
  rate: 0,
  priceTypeId: 0,
  manualRateComment: '',
  isUpdateOnlineToo: false,
});

export interface SpecialRatesGridProps {
  resourceCode: string;
  titleKey: MessageKey;
  getRows: () => Promise<SpecialRate[]>;
  saveBatch: (
    added: SpecialRate[],
    modified: SpecialRate[],
    deletedIds: number[],
  ) => Promise<SpecialRate[]>;
  addRange: (req: SpecialRateRangeRequest) => Promise<SpecialRate[]>;
}

export function SpecialRatesGrid(props: SpecialRatesGridProps) {
  const { resourceCode, titleKey, getRows, saveBatch, addRange } = props;

  const {
    data: priceTypes,
    loading: priceTypesLoading,
    error: priceTypesError,
  } = usePriceTypes();

  // Bump to force LopCrudPage to re-fetch after a range add. getRows is wrapped
  // so its identity changes with the version, which re-runs the data fetch.
  const [refreshVersion, setRefreshVersion] = useState(0);

  // Range-add form state.
  const [assetId, setAssetId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [tillDate, setTillDate] = useState('');
  const [rate, setRate] = useState('');
  const [priceTypeId, setPriceTypeId] = useState('');
  const [manualRateComment, setManualRateComment] = useState('');
  const [isUpdateOnlineToo, setIsUpdateOnlineToo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);

  const wrappedGetRows = useCallback(
    () => getRows(),
    // refreshVersion intentionally part of the identity so a range add re-fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getRows, refreshVersion],
  );

  const columnDefs = useMemo<ColDef<SpecialRate>[]>(() => {
    if (!priceTypes) return [];

    // priceTypeId is REQUIRED — no none option. Keyed by "<id> - <name>".
    const priceTypeValues = priceTypes.map(
      (p) => `${p.priceTypeId} - ${p.priceTypeName}`,
    );
    const priceTypeLabelById = new Map<number, string>(
      priceTypes.map((p) => [
        p.priceTypeId,
        `${p.priceTypeId} - ${p.priceTypeName}`,
      ]),
    );
    const priceTypeIdByLabel = new Map<string, number>(
      priceTypes.map((p) => [
        `${p.priceTypeId} - ${p.priceTypeName}`,
        p.priceTypeId,
      ]),
    );

    return [
      {
        field: 'recId',
        headerName: t('specialRates.columns.recId'),
        width: 110,
        editable: false,
        cellDataType: 'number',
        sort: 'asc',
      },
      {
        field: 'assetId',
        headerName: t('specialRates.columns.assetId'),
        width: 120,
        // 65K assets — plain number input, no dropdown (mirrors AssetsPage idxId).
        editable: true,
        cellDataType: 'number',
      },
      {
        field: 'dDate',
        headerName: t('specialRates.columns.dDate'),
        width: 160,
        editable: true,
        ...dateColumnProps<SpecialRate>('dDate'),
      },
      {
        field: 'rate',
        headerName: t('specialRates.columns.rate'),
        width: 130,
        editable: true,
        cellDataType: 'number',
      },
      {
        field: 'priceTypeId',
        headerName: t('specialRates.columns.priceTypeId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: priceTypeValues },
        valueGetter: (p: ValueGetterParams<SpecialRate>) => {
          if (!p.data) return '';
          return (
            priceTypeLabelById.get(p.data.priceTypeId) ??
            String(p.data.priceTypeId)
          );
        },
        valueSetter: (p: ValueSetterParams<SpecialRate>) => {
          if (!p.data) return false;
          const newId = priceTypeIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.priceTypeId = newId;
          return true;
        },
      },
      {
        field: 'manualRateComment',
        headerName: t('specialRates.columns.manualRateComment'),
        flex: 1,
        editable: true,
      },
      {
        field: 'isUpdateOnlineToo',
        headerName: t('specialRates.columns.isUpdateOnlineToo'),
        width: 130,
        editable: true,
        cellDataType: 'boolean',
      },
    ];
  }, [priceTypes]);

  const priceTypeOptions = useMemo(
    () =>
      (priceTypes ?? []).map((p) => ({
        value: p.priceTypeId,
        label: `${p.priceTypeId} - ${p.priceTypeName}`,
      })),
    [priceTypes],
  );

  const handleAddRange = useCallback(async () => {
    setFormError(null);
    const assetIdNum = Number(assetId);
    const rateNum = Number(rate);
    const priceTypeIdNum = Number(priceTypeId);
    if (
      !assetId ||
      Number.isNaN(assetIdNum) ||
      !fromDate ||
      !tillDate ||
      rate === '' ||
      Number.isNaN(rateNum) ||
      !priceTypeId ||
      Number.isNaN(priceTypeIdNum)
    ) {
      setFormError(t('specialRates.range.validation'));
      return;
    }
    setSubmitting(true);
    try {
      await addRange({
        assetId: assetIdNum,
        fromDate,
        tillDate,
        rate: rateNum,
        priceTypeId: priceTypeIdNum,
        manualRateComment,
        isUpdateOnlineToo,
      });
      // Trigger LopCrudPage re-fetch by changing getRows identity.
      setRefreshVersion((v) => v + 1);
      // Clear the form (keep priceType selection for convenience).
      setAssetId('');
      setFromDate('');
      setTillDate('');
      setRate('');
      setManualRateComment('');
      setIsUpdateOnlineToo(false);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }, [
    addRange,
    assetId,
    fromDate,
    tillDate,
    rate,
    priceTypeId,
    manualRateComment,
    isUpdateOnlineToo,
  ]);

  if (priceTypesError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('specialRates.errorPrefix', { message: priceTypesError })}
        </div>
      </div>
    );
  }

  if (priceTypesLoading || !priceTypes) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <div className="special-rates-grid">
      <section className="special-rates-range">
        <h2 className="special-rates-range__title">
          {t('specialRates.range.addRange')}
        </h2>
        <div className="special-rates-range__fields">
          <label className="special-rates-range__field">
            <span>{t('specialRates.columns.assetId')}</span>
            <div className="special-rates-range__asset">
              <input
                type="number"
                value={assetId}
                onChange={(e) => setAssetId(e.target.value)}
              />
              <button
                type="button"
                className="special-rates-range__asset-pick"
                aria-label={t('specialRates.range.pickAsset')}
                title={t('specialRates.range.pickAsset')}
                onClick={() => setAssetPickerOpen(true)}
              >
                <Search size={16} />
              </button>
            </div>
          </label>
          <label className="special-rates-range__field">
            <span>{t('specialRates.range.fromDate')}</span>
            <LopDateInput value={fromDate} onChange={setFromDate} />
          </label>
          <label className="special-rates-range__field">
            <span>{t('specialRates.range.tillDate')}</span>
            <LopDateInput value={tillDate} onChange={setTillDate} />
          </label>
          <label className="special-rates-range__field">
            <span>{t('specialRates.columns.rate')}</span>
            <input
              type="number"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </label>
          <label className="special-rates-range__field">
            <span>{t('specialRates.columns.priceTypeId')}</span>
            <select
              value={priceTypeId}
              onChange={(e) => setPriceTypeId(e.target.value)}
            >
              <option value="">—</option>
              {priceTypeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="special-rates-range__field special-rates-range__field--wide">
            <span>{t('specialRates.columns.manualRateComment')}</span>
            <input
              type="text"
              value={manualRateComment}
              onChange={(e) => setManualRateComment(e.target.value)}
            />
          </label>
          <label className="special-rates-range__field special-rates-range__field--check">
            <input
              type="checkbox"
              checked={isUpdateOnlineToo}
              onChange={(e) => setIsUpdateOnlineToo(e.target.checked)}
            />
            <span>{t('specialRates.columns.isUpdateOnlineToo')}</span>
          </label>
          <button
            type="button"
            className="special-rates-range__button"
            onClick={() => {
              void handleAddRange();
            }}
            disabled={submitting}
          >
            {t('specialRates.range.rangeButton')}
          </button>
        </div>
        {formError && (
          <div className="special-rates-range__error">
            {t('specialRates.errorPrefix', { message: formError })}
          </div>
        )}
      </section>
      <div className="special-rates-grid__crud">
        <LopCrudPage<SpecialRate>
          resourceCode={resourceCode}
          titleKey={titleKey}
          idField="recId"
          getRows={wrappedGetRows}
          saveBatch={(added, modified, deletedIds) =>
            saveBatch(added, modified, deletedIds as number[])
          }
          columnDefs={columnDefs}
          newRowDefaults={newSpecialRateDefaults}
        />
      </div>
      {assetPickerOpen && (
        <LopSearchModal
          title={t('specialRates.range.pickAsset')}
          table="ASSETS"
          columns={[
            { field: 'assetId', headerName: t('assets.columns.assetId') },
            { field: 'assetName', headerName: t('assets.columns.assetName') },
            { field: 'symbol', headerName: t('assets.columns.symbol') },
          ]}
          onSelect={(row) => {
            const picked = row.assetId;
            if (picked != null) setAssetId(String(picked));
          }}
          onClose={() => setAssetPickerOpen(false)}
        />
      )}
    </div>
  );
}
